import { Injectable } from '@angular/core';

export interface CameraOptions {
   facingMode: 'user' | 'environment';
   torch?: boolean;
   resolution?: { width: number; height: number };
}

@Injectable({ providedIn: 'root' })
export class CameraService {
   private _activeStream: MediaStream | null = null;

   get activeStream(): MediaStream | null {
      return this._activeStream;
   }

   async getCameraStream(options: CameraOptions): Promise<MediaStream> {
      if (this._activeStream) {
         this.stopStream();
      }

      const constraints: MediaStreamConstraints = {
         video: {
            facingMode: options.facingMode,
            ...(options.resolution?.width && { width: { ideal: options.resolution.width } }),
            ...(options.resolution?.height && { height: { ideal: options.resolution.height } }),
            ...(options.torch !== undefined ? { advanced: [{ torch: options.torch } as any] } : {})
         }
      };

      try {
         this._activeStream = await navigator.mediaDevices.getUserMedia(constraints);
         return this._activeStream;
      } catch (error) {
         console.error('Camera access error:', error);
         throw new Error('Could not access camera');
      }
   }

   stopStream(): void {
      if (this._activeStream) {
         this._activeStream.getTracks().forEach(track => track.stop());
         this._activeStream = null;
      }
   }

   async toggleTorch(enable: boolean): Promise<boolean> {
      if (!this._activeStream) return false;

      const track = this._activeStream.getVideoTracks()[0];
      if (!track) return false;

      try {
         await track.applyConstraints({
            advanced: [{ torch: enable } as any]
         });
         return true;
      } catch (error) {
         console.error('Torch toggle failed:', error);
         return false;
      }
   }

   isTorchSupported(): boolean {
      if (!this._activeStream) return false;
      const track = this._activeStream.getVideoTracks()[0];
      if (!track) return false;

      const capabilities = track.getCapabilities();
      return 'torch' in capabilities && !!capabilities.torch;
   }
}